/**
 * =============================================================================
 * SCRIPT-NAME:   gsc_kpi.js
 * PROJEKT:       Freiseinapp - GSC KPI-Dashboard
 * MODUL:         GSC Search-Console-Daten je LB/LP, wöchentlich
 * VERSION:       v0.7
 * DATUM:         2026-06-04 19:10
 * AENDERUNGEN v0.7:
 * - Sparkline-Spalte (SVG, 8 Wochen Impressionen + Klicks-Punkte)
 *   gibt visuelles Bild des Wochen-Trends pro URL — entkoppelt vom Δ Vorwoche
 * v0.6:
 * - Notiz-week_start kommt aus detail.week_start (nicht meta.last_week)
 *   passt damit zur angezeigten Daten-Woche im Detail-View
 * v0.5:
 * - Pillar-Trend-Chart oben (Chart.js, 5 Pillars × N Wochen)
 * - Detail-Accordion bei Klick auf Prefix: 6 Dimensionen + 12-Wochen-Trend
 * - Notizen-CRUD pro URL (Liste + Neu + Bearbeiten + Löschen)
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import BackButton from "./BackButton";
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler
} from 'chart.js';
import "./gsc_kpi.css";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const API_BASE = "https://freiseinapp.de/api";
const PILLARS  = ['VA', 'EF', 'EA', 'SO', 'PBC'];
const PILLAR_COLORS = {
    VA:  '#dc2626',
    EF:  '#0ea5e9',
    EA:  '#16a34a',
    SO:  '#9333ea',
    PBC: '#d97706',
};

const fmtNum   = (n) => Number(n || 0).toLocaleString('de-DE');
const fmtPct   = (n) => (Number(n || 0) * 100).toFixed(2).replace('.', ',') + ' %';
const fmtPos   = (n) => Number(n || 0).toFixed(1).replace('.', ',');
/* ========================================================================
 * SPARKLINE-Komponente (SVG)
 * ====================================================================== */
function Sparkline({ data, width = 90, height = 26 }) {
    if (!data || data.length === 0) return null;
    const impr = data.map(d => Number(d.impressions || 0));
    const clks = data.map(d => Number(d.clicks || 0));
    const maxImp = Math.max(...impr, 1);
    const lastIdx = data.length - 1;

    const xFor = (i) => (i / (data.length - 1)) * (width - 6) + 3;
    const yFor = (v) => height - 3 - (v / maxImp) * (height - 6);

    // Line + Fläche für Impressionen
    const points = impr.map((v, i) => [xFor(i), yFor(v)]);
    const linePath = points.map((p, i) => (i === 0 ? 'M' : 'L') + p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' ');
    const fillPath = linePath + ` L${points[lastIdx][0].toFixed(1)},${height - 1} L${points[0][0].toFixed(1)},${height - 1} Z`;

    return (
        <svg width={width} height={height} className="gsc-sparkline" role="img" aria-label="Trend letzte 8 Wochen">
            <path d={fillPath} fill="rgba(37, 99, 235, 0.12)" />
            <path d={linePath} stroke="#2563eb" strokeWidth="1.5" fill="none" />
            {points.map((p, i) => {
                const c = clks[i];
                const isLast = i === lastIdx;
                if (c > 0) {
                    return <circle key={i} cx={p[0]} cy={p[1]} r="2" fill="#16a34a" stroke="white" strokeWidth="0.5" />;
                }
                if (isLast) {
                    return <circle key={i} cx={p[0]} cy={p[1]} r="2" fill="#dc2626" stroke="white" strokeWidth="0.5" />;
                }
                return null;
            })}
        </svg>
    );
}

const fmtDelta = (n, type) => {
    const v = Number(n || 0);
    if (v === 0) return <span className="gsc-delta gsc-delta-zero">±0</span>;
    const positive = type === 'position' ? v < 0 : v > 0;
    const cls  = positive ? 'gsc-delta-up' : 'gsc-delta-down';
    const sign = v > 0 ? '+' : '';
    let formatted;
    if (type === 'ctr')           formatted = (v * 100).toFixed(2).replace('.', ',') + ' %';
    else if (type === 'position') formatted = v.toFixed(1).replace('.', ',');
    else                          formatted = fmtNum(v);
    return <span className={`gsc-delta ${cls}`}>{sign}{formatted}</span>;
};

/* ========================================================================
 * PILLAR-TREND-CHART
 * ====================================================================== */
function PillarTrendChart({ token }) {
    const [data, setData]     = useState(null);
    const [metric, setMetric] = useState('impressions'); // 'clicks' | 'impressions'

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res  = await fetch(`${API_BASE}/gsc_get_pillar_trend.php?weeks=8`, {
                    headers: { "Authorization": `Bearer ${token}` }
                });
                const json = await res.json();
                if (!cancelled && json.success) setData(json);
            } catch (e) { /* swallow */ }
        })();
        return () => { cancelled = true; };
    }, [token]);

    if (!data || !data.weeks || data.weeks.length === 0) {
        return <div className="gsc-pillar-chart-empty">Pillar-Trend: noch keine Daten.</div>;
    }

    const chartData = {
        labels: data.weeks,
        datasets: PILLARS.map(p => ({
            label: p,
            data: data.pillars[p]?.[metric] || [],
            borderColor: PILLAR_COLORS[p],
            backgroundColor: PILLAR_COLORS[p] + '33',
            tension: 0.3,
            fill: false,
            pointRadius: 3,
        })),
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'right', labels: { boxWidth: 12, font: { size: 12 } } },
            title: { display: false },
            tooltip: { mode: 'index', intersect: false },
        },
        scales: { y: { beginAtZero: true } },
    };

    return (
        <div className="gsc-pillar-chart">
            <div className="gsc-pillar-chart-header">
                <h3>Pillar-Trend · 8 Wochen</h3>
                <div className="gsc-pillar-metric-switch">
                    <button type="button"
                        className={`gsc-metric-btn ${metric === 'impressions' ? 'active' : ''}`}
                        onClick={() => setMetric('impressions')}>Impressionen</button>
                    <button type="button"
                        className={`gsc-metric-btn ${metric === 'clicks' ? 'active' : ''}`}
                        onClick={() => setMetric('clicks')}>Klicks</button>
                </div>
            </div>
            <div className="gsc-pillar-chart-body">
                <Line data={chartData} options={options} />
            </div>
        </div>
    );
}

/* ========================================================================
 * DETAIL-VIEW (Accordion-Inhalt)
 * ====================================================================== */
function DetailView({ url, weekStart, token }) {
    const [detail, setDetail] = useState(null);
    const [notes, setNotes]   = useState([]);
    const [loading, setLoading] = useState(true);
    const [newNote, setNewNote] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editingText, setEditingText] = useState('');

    const reloadDetail = useCallback(async () => {
        setLoading(true);
        try {
            const ds = await fetch(`${API_BASE}/gsc_get_detail.php?url=${encodeURIComponent(url)}`, {
                headers: { "Authorization": `Bearer ${token}` }
            }).then(r => r.json());
            const ns = await fetch(`${API_BASE}/gsc_notes.php?url=${encodeURIComponent(url)}`, {
                headers: { "Authorization": `Bearer ${token}` }
            }).then(r => r.json());
            if (ds.success) setDetail(ds);
            if (ns.success) setNotes(ns.data || []);
        } catch (e) { /* swallow */ }
        setLoading(false);
    }, [url, token]);

    useEffect(() => { reloadDetail(); }, [reloadDetail]);

    const addNote = async () => {
        const text = newNote.trim();
        if (!text) return;
        // Notiz auf die im Detail angezeigte Woche beziehen (nicht meta.last_week)
        const noteWeek = detail?.week_start || weekStart;
        await fetch(`${API_BASE}/gsc_notes.php`, {
            method: 'POST',
            headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify({ url, week_start: noteWeek, note: text }),
        });
        setNewNote('');
        reloadDetail();
    };

    const saveEdit = async (id) => {
        const text = editingText.trim();
        if (!text) return;
        await fetch(`${API_BASE}/gsc_notes.php?id=${id}`, {
            method: 'PUT',
            headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify({ note: text }),
        });
        setEditingId(null);
        setEditingText('');
        reloadDetail();
    };

    const deleteNote = async (id) => {
        if (!window.confirm('Notiz wirklich löschen?')) return;
        await fetch(`${API_BASE}/gsc_notes.php?id=${id}`, {
            method: 'DELETE',
            headers: { "Authorization": `Bearer ${token}` },
        });
        reloadDetail();
    };

    if (loading) return <div className="gsc-detail-loading">Lade Detail-Daten…</div>;
    if (!detail) return <div className="gsc-detail-loading">Keine Daten.</div>;

    const dims = detail.dimensions || {};
    const trend = detail.trend || [];

    // Mini-Trend-Chart
    const trendChartData = {
        labels: trend.map(t => t.week_start),
        datasets: [
            { label: 'Klicks',     data: trend.map(t => t.clicks),      borderColor: '#16a34a', tension: 0.3, fill: false, pointRadius: 3 },
            { label: 'Impressionen', data: trend.map(t => t.impressions), borderColor: '#2563eb', tension: 0.3, fill: false, pointRadius: 3, yAxisID: 'y1' },
        ],
    };
    const trendOpts = {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { position: 'top', labels: { boxWidth: 12, font: { size: 11 } } } },
        scales: {
            y:  { beginAtZero: true, position: 'left',  title: { display: true, text: 'Klicks' } },
            y1: { beginAtZero: true, position: 'right', title: { display: true, text: 'Impr.' }, grid: { drawOnChartArea: false } },
        },
    };

    const renderDimTable = (rows, label1) => (
        <table className="gsc-dim-table">
            <thead><tr>
                <th>{label1}</th>
                <th className="gsc-num">Klicks</th>
                <th className="gsc-num">Impr.</th>
                <th className="gsc-num">CTR</th>
                <th className="gsc-num">Pos.</th>
            </tr></thead>
            <tbody>
                {rows.length === 0 && <tr><td colSpan="5" className="gsc-empty">— keine Daten —</td></tr>}
                {rows.slice(0, 20).map((r, i) => (
                    <tr key={i}>
                        <td>{r.dimension_key || '—'}</td>
                        <td className="gsc-num">{fmtNum(r.clicks)}</td>
                        <td className="gsc-num">{fmtNum(r.impressions)}</td>
                        <td className="gsc-num">{fmtPct(r.ctr)}</td>
                        <td className="gsc-num">{fmtPos(r.position)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    return (
        <div className="gsc-detail">
            <div className="gsc-detail-info">
                <strong>Detail für Woche: {detail.week_start}</strong>
                {detail.meta?.fs1 && <span> · FS1: {detail.meta.fs1}</span>}
            </div>

            {trend.length > 1 && (
                <div className="gsc-detail-section">
                    <h4>📈 Verlauf (letzte {trend.length} Wochen)</h4>
                    <div className="gsc-mini-chart">
                        <Line data={trendChartData} options={trendOpts} />
                    </div>
                </div>
            )}

            <div className="gsc-detail-grid">
                <div className="gsc-detail-section">
                    <h4>🔍 Top-Suchanfragen</h4>
                    {renderDimTable(dims.query || [], 'Query')}
                </div>
                <div className="gsc-detail-section">
                    <h4>🌍 Länder</h4>
                    {renderDimTable(dims.country || [], 'Land')}
                </div>
                <div className="gsc-detail-section">
                    <h4>📱 Geräte</h4>
                    {renderDimTable(dims.device || [], 'Gerät')}
                </div>
                <div className="gsc-detail-section">
                    <h4>📅 Tagesverlauf</h4>
                    {renderDimTable(dims.date || [], 'Tag')}
                </div>
            </div>

            <div className="gsc-detail-section gsc-notes-section">
                <h4>📝 Notizen</h4>

                <div className="gsc-note-add">
                    <textarea
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        placeholder="Neue Notiz für diese URL & Woche…"
                        rows="2"
                    />
                    <button type="button" className="gsc-btn-primary" onClick={addNote} disabled={!newNote.trim()}>
                        Speichern
                    </button>
                </div>

                {notes.length === 0 && <div className="gsc-notes-empty">Noch keine Notizen.</div>}

                <ul className="gsc-notes-list">
                    {notes.map(n => (
                        <li key={n.id} className="gsc-note-item">
                            <div className="gsc-note-meta">
                                <span className="gsc-note-week">📅 {n.week_start}</span>
                                {n.dimension && <span className="gsc-note-dim"> · {n.dimension}</span>}
                                <span className="gsc-note-time"> · {n.updated_at}</span>
                            </div>
                            {editingId === n.id ? (
                                <>
                                    <textarea
                                        value={editingText}
                                        onChange={(e) => setEditingText(e.target.value)}
                                        rows="3"
                                    />
                                    <div className="gsc-note-actions">
                                        <button type="button" className="gsc-btn-primary" onClick={() => saveEdit(n.id)}>Speichern</button>
                                        <button type="button" className="gsc-btn-ghost" onClick={() => { setEditingId(null); setEditingText(''); }}>Abbrechen</button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="gsc-note-text">{n.note}</div>
                                    <div className="gsc-note-actions">
                                        <button type="button" className="gsc-btn-ghost" onClick={() => { setEditingId(n.id); setEditingText(n.note); }}>Bearbeiten</button>
                                        <button type="button" className="gsc-btn-danger" onClick={() => deleteNote(n.id)}>Löschen</button>
                                    </div>
                                </>
                            )}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

/* ========================================================================
 * HAUPT-KOMPONENTE
 * ====================================================================== */
// KI-Wochen-Auswertung (Haiku): Analyse-Summary + Handlungsempfehlungen mit Status & Notiz
function WeeklyAnalysis({ token }) {
    const [recs, setRecs] = useState([]);
    const [week, setWeek] = useState('');
    const [loading, setLoading] = useState(true);
    const [noteEdits, setNoteEdits] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/gsc_recommendations.php`, { headers: { Authorization: `Bearer ${token}` } });
            const json = await res.json();
            if (json.success) {
                const data = json.data || [];
                setRecs(data);
                setWeek(data.length ? data[0].week_label : '');
                const ne = {}; data.forEach(r => { ne[r.id] = r.note || ''; });
                setNoteEdits(ne);
            }
        } catch (e) { /* still */ }
        setLoading(false);
    }, [token]);
    useEffect(() => { load(); }, [load]);

    const setStatus = async (id, status) => {
        await fetch(`${API_BASE}/gsc_recommendations.php`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ id, status }) });
        setRecs(rs => rs.map(r => r.id === id ? { ...r, status } : r));
    };
    const saveNote = async (id) => {
        const note = noteEdits[id] ?? '';
        await fetch(`${API_BASE}/gsc_recommendations.php`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ id, note }) });
        setRecs(rs => rs.map(r => r.id === id ? { ...r, note } : r));
    };

    if (loading || !recs.length) return null;
    const summary = recs.find(r => Number(r.rank_no) === 0);
    const items = recs.filter(r => Number(r.rank_no) >= 1);
    const STATUS = [['applied', '✓ erledigt', '#16a34a'], ['skipped', '⊘ später', '#9CA3AF'], ['open', '○ offen', '#B8933A']];

    return (
        <div style={{ border: '1px solid #B8933A', borderRadius: 10, padding: '16px 18px', margin: '0 0 18px', background: '#FCFBF7' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <h3 style={{ margin: 0 }}>🧠 KI-Wochen-Auswertung</h3>
                <span style={{ fontSize: '0.85rem', color: '#6B7280' }}>{week}</span>
            </div>
            {summary && <div style={{ fontSize: '0.92rem', lineHeight: 1.55 }} dangerouslySetInnerHTML={{ __html: summary.body }} />}
            {items.length > 0 && (
                <div style={{ marginTop: 10 }}>
                    <h4 style={{ margin: '0 0 4px' }}>Handlungsempfehlungen</h4>
                    {items.map(r => (
                        <div key={r.id} style={{ borderTop: '1px solid #ECE7DA', padding: '10px 0' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                                <div style={{ flex: 1 }}>
                                    <strong>{r.title}</strong>
                                    <div style={{ fontSize: '0.9rem', color: '#374151', marginTop: 2 }}>{r.body}</div>
                                </div>
                                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                                    {STATUS.map(([s, label, col]) => (
                                        <button key={s} onClick={() => setStatus(r.id, s)}
                                            style={{ background: r.status === s ? col : '#fff', color: r.status === s ? '#fff' : col, border: `1px solid ${col}`, borderRadius: 4, padding: '2px 8px', fontSize: '0.75rem', cursor: 'pointer' }}>
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <input type="text" value={noteEdits[r.id] ?? ''} placeholder="Notiz: was hast du gemacht?"
                                onChange={e => setNoteEdits(n => ({ ...n, [r.id]: e.target.value }))}
                                onBlur={() => saveNote(r.id)}
                                style={{ width: '100%', marginTop: 6, fontSize: '0.85rem', padding: '3px 6px', border: '1px solid #D1D5DB', borderRadius: 4 }} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function GscKpi() {
    const token = localStorage.getItem("token");

    const [tab, setTab]             = useState('LP');
    const [pillar, setPillar]       = useState('');
    const [rows, setRows]           = useState([]);
    const [meta, setMeta]           = useState({ this_week: '', last_week: '', count: 0, available_weeks: [] });
    const [loading, setLoading]     = useState(false);
    const [error, setError]         = useState('');
    const [reloadKey, setReloadKey] = useState(0);
    const [expanded, setExpanded]   = useState(null);  // url die expandiert ist
    const [compareWeek, setCompareWeek] = useState('');  // '' = Vorwoche (Standard), sonst gewählte Vergleichswoche

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            setLoading(true);
            setError('');
            try {
                const endpoint = tab === 'LP' ? 'gsc_get_lp.php' : 'gsc_get_lb.php';
                const params   = new URLSearchParams();
                if (tab === 'LB' && pillar) params.set('pillar', pillar);
                if (compareWeek) params.set('compare', compareWeek);
                const qs       = params.toString() ? `?${params.toString()}` : '';
                const url      = `${API_BASE}/${endpoint}${qs}`;
                const res      = await fetch(url, { headers: { "Authorization": `Bearer ${token}` } });
                const json     = await res.json();
                if (cancelled) return;
                if (json.success) {
                    setRows(json.data || []);
                    setMeta({ this_week: json.this_week, last_week: json.last_week, count: json.count, available_weeks: json.available_weeks || [] });
                } else {
                    setError(json.message || 'Unbekannter Fehler');
                    setRows([]);
                }
            } catch (e) {
                if (!cancelled) {
                    setError('Verbindung zur API fehlgeschlagen: ' + e.message);
                    setRows([]);
                }
            }
            if (!cancelled) setLoading(false);
        };
        run();
        return () => { cancelled = true; };
    }, [tab, pillar, token, reloadKey, compareWeek]);

    const handleTabClick = (newTab) => {
        if (newTab === 'LP') setPillar('');
        setTab(newTab);
        setExpanded(null);
    };

    return (
        <div className="gsc-page-wrapper">
            <div className="gsc-content-box">

                <header className="gsc-header">
                    <h2 className="gsc-title">📊 GSC KPI-Dashboard</h2>
                    <p className="gsc-subtitle">Search-Console-Daten je LB / LP · wöchentlich</p>
                </header>

                <WeeklyAnalysis token={token} />

                <PillarTrendChart token={token} />

                <div className="gsc-toolbar">
                    <div className="gsc-tabs">
                        <button type="button" className={`gsc-tab ${tab === 'LP' ? 'gsc-tab-active' : ''}`}
                                onClick={() => handleTabClick('LP')}>Landing-Pages (LP)</button>
                        <button type="button" className={`gsc-tab ${tab === 'LB' ? 'gsc-tab-active' : ''}`}
                                onClick={() => handleTabClick('LB')}>Blog-Artikel (LB)</button>
                    </div>

                    {tab === 'LB' && (
                        <div className="gsc-filter">
                            <label>Pillar:</label>
                            <select value={pillar} onChange={(e) => { setPillar(e.target.value); setExpanded(null); }}>
                                <option value="">alle</option>
                                {PILLARS.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </div>
                    )}

                    <div className="gsc-meta-info">
                        <span>Woche: {meta.this_week || '—'}</span>
                        <span> · Vergleich mit: </span>
                        <select className="gsc-compare-select" value={compareWeek}
                                onChange={e => setCompareWeek(e.target.value)}
                                style={{ fontSize: 'inherit', padding: '1px 4px', margin: '0 2px' }}>
                            <option value="">Vorwoche (Standard)</option>
                            {(meta.available_weeks || []).map(w => <option key={w} value={w}>{w}</option>)}
                        </select>
                        <span>({meta.last_week || '—'})</span>
                        <span> · {meta.count} Einträge</span>
                        <button type="button" className="gsc-refresh" onClick={() => setReloadKey(k => k+1)} disabled={loading}>
                            {loading ? '...' : '↻'}
                        </button>
                    </div>
                </div>

                {error && <div className="gsc-error">⚠ {error}</div>}

                <div className="gsc-table-wrap">
                    <table className="gsc-table">
                        <thead>
                            <tr>
                                <th></th>
                                <th>Prefix</th>
                                {tab === 'LB' && <th>Pillar</th>}
                                <th>Titel · URL</th>
                                <th className="gsc-status-col">Status</th>
                                <th>Eingereicht</th>
                                <th className="gsc-spark-col">Trend 8W</th>
                                <th className="gsc-num">Klicks</th>
                                <th className="gsc-num">Δ</th>
                                <th className="gsc-num">Impr.</th>
                                <th className="gsc-num">Δ</th>
                                <th className="gsc-num">CTR</th>
                                <th className="gsc-num">Ø Pos.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && !loading && (
                                <tr><td colSpan={tab === 'LB' ? 13 : 12} className="gsc-empty">
                                    Keine Einträge gefunden.
                                </td></tr>
                            )}
                            {rows.map((r) => {
                                const isOpen = expanded === r.url;
                                return (
                                    <React.Fragment key={r.url}>
                                        <tr className={`gsc-row ${isOpen ? 'gsc-row-open' : ''}`}
                                            onClick={() => setExpanded(isOpen ? null : r.url)}>
                                            <td className="gsc-expand">{isOpen ? '▾' : '▸'}</td>
                                            <td className="gsc-prefix">{r.prefix}</td>
                                            {tab === 'LB' && <td>{r.pillar || '—'}</td>}
                                            <td className="gsc-title-cell">
                                                <div className="gsc-row-title">{r.title || '—'}</div>
                                                <a href={r.url} target="_blank" rel="noopener noreferrer" className="gsc-row-url" onClick={(e) => e.stopPropagation()}>{r.url}</a>
                                            </td>
                                            <td className="gsc-status-col">
                                                <span className={`gsc-flag ${r.status_g ? 'on' : 'off'}`} title="GSC eingereicht">G</span>
                                                <span className={`gsc-flag ${r.status_x ? 'on' : 'off'}`} title="Rank Math indexierbar">X</span>
                                                <span className={`gsc-flag ${r.status_b ? 'on' : 'off'}`} title="Breadcrumb">B</span>
                                                <span className={`gsc-flag ${r.status_s ? 'on' : 'off'}`} title="Schema">S</span>
                                                <span className={`gsc-flag ${r.status_l ? 'on' : 'off'}`} title="LP/LB-ID">L</span>
                                            </td>
                                            <td className="gsc-date">{r.submitted_date || '—'}</td>
                                            <td className="gsc-spark-col"><Sparkline data={r.sparkline} /></td>
                                            <td className="gsc-num">{fmtNum(r.clicks)}</td>
                                            <td className="gsc-num">{fmtDelta(r.clicks_delta, 'clicks')}</td>
                                            <td className="gsc-num">{fmtNum(r.impressions)}</td>
                                            <td className="gsc-num">{fmtDelta(r.impressions_delta, 'impressions')}</td>
                                            <td className="gsc-num">{fmtPct(r.ctr)}</td>
                                            <td className="gsc-num">{fmtPos(r.position)}</td>
                                        </tr>
                                        {isOpen && (
                                            <tr className="gsc-detail-row">
                                                <td colSpan={tab === 'LB' ? 13 : 12}>
                                                    <DetailView url={r.url} weekStart={meta.last_week} token={token} />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="gsc-footer">
                    <BackButton to="/admin" />
                </div>

            </div>
        </div>
    );
}
